// Root of the UI: top bar and menu tabs, loading and pause screens, the end
// of run panel, scoreboard and vote banner in game, dialogs and toasts.
// Escape always goes back one level; arrow keys move the focus (focusNav.ts).

import { signal } from '@preact/signals';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { App } from '../app';
import { bindLabel } from '../game/binds';
import { Button } from './components/Button';
import { CreateRoomDialog } from './components/CreateRoomDialog';
import { TextInput } from './components/Field';
import { Icon } from './components/Icon';
import { Modal } from './components/Modal';
import { RunResultCard } from './components/RunResultCard';
import { Scoreboard } from './components/Scoreboard';
import { Tabs } from './components/Tabs';
import { ToastStack } from './components/Toast';
import { AppContext, useApp } from './context';
import { stripColors } from './etColors';
import { useFocusNav } from './focusNav';
import { HomeScreen } from './screens/Home';
import { LoadingScreen } from './screens/Loading';
import { MapsScreen } from './screens/Maps';
import { PauseScreen } from './screens/Pause';
import { RoomsScreen } from './screens/Rooms';
import { SettingsScreen } from './screens/Settings';
import { createRoomDialog, loading, modal, net, pauseView, phase, runResult, runResultVisible, scoreboardOpen, selectedMap, settings, tab, type Tab } from './state';
import { AdminScreen } from './screens/Admin';

const TABS: { id: Tab; label: string }[] = [
  { id: 'play', label: 'Play' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'maps', label: 'Maps' },
  { id: 'settings', label: 'Settings' },
];

/** The change name dialog is open. */
const nameDialog = signal(false);

function NameDialog() {
  const app = useApp();
  const [name, setName] = useState(settings.peek().playerName);
  const formRef = useRef<HTMLFormElement>(null);
  const close = () => (nameDialog.value = false);
  useEffect(() => formRef.current?.querySelector('input')?.focus(), []);
  return (
    <div class="modal-backdrop layer" data-layer="modal" role="dialog" aria-modal="true" aria-labelledby="name-title" onClick={(e) => e.target === e.currentTarget && close()}>
      <form
        ref={formRef}
        class="modal"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await app.setName(name)) close();
        }}
      >
        <h2 id="name-title" class="modal-title">
          Player name
        </h2>
        <p class="modal-body">Your name is reserved for you on the server; your times are kept under it.</p>
        <TextInput value={name} onChange={setName} maxLength={24} label="Player name" />
        <div class="modal-actions">
          <Button variant="ghost" onClick={close} hint="ESC">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!name.trim()}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}

function TopBar() {
  const app = useApp();
  const n = net.value;
  const status = n.status === 'online' ? n.serverName : n.status === 'connecting' ? 'Connecting…' : 'Offline';
  return (
    <header class="topbar">
      <div class="logo" aria-label="TJgame">
        TJ<span>game</span>
      </div>
      <Tabs
        items={n.client?.admin ? [...TABS, { id: 'admin' as Tab, label: 'Admin' }] : TABS}
        value={tab.value}
        onChange={(t) => {
          tab.value = t;
          selectedMap.value = null;
        }}
        label="Main menu"
        class="topbar-tabs"
      />
      <div class="topbar-right">
        <button
          type="button"
          class={`server-status ${n.status}`}
          onClick={() => (n.status === 'offline' ? app.connect() : (tab.value = 'rooms'))}
          title={n.status === 'offline' ? 'Offline: click to reconnect' : `Server: ${status}`}
          aria-label={`Server: ${status}`}
        >
          <span class="dot" aria-hidden="true" />
          <span>{status}</span>
          {n.status === 'online' && n.ping !== null && <span class="ping mono">{n.ping} ms</span>}
        </button>
        <button type="button" class="nick" onClick={() => (nameDialog.value = true)} title="Change your name">
          <Icon name="users" size={16} />
          {settings.value.playerName}
        </button>
      </div>
    </header>
  );
}

function Menu() {
  const t = tab.value;
  return (
    <div class="menu">
      <TopBar />
      <main class="menu-body" key={t}>
        {t === 'play' && <HomeScreen />}
        {t === 'rooms' && <RoomsScreen />}
        {t === 'maps' && <MapsScreen />}
        {t === 'settings' && <SettingsScreen />}
        {t === 'admin' && <AdminScreen />}
      </main>
    </div>
  );
}

/** Compact vote banner while playing (the vote keys are shown). */
function VoteBanner() {
  const n = net.value;
  const v = n.room?.vote;
  if (!v) return null;
  const binds = settings.value.binds;
  const title = n.maps.find((m) => m.name === v.map)?.longname;
  return (
    <aside class="vote-banner" aria-live="polite">
      <span class="eyebrow">Vote</span> Change map to <b>{title ? stripColors(title) : v.map}</b>
      <span class="mono">
        {' '}
        ✓ {v.yes}/{v.needed}
      </span>
      <span class="muted">
        {' '}
        · {bindLabel(binds, 'voteYes')} yes · {bindLabel(binds, 'voteNo')} no
      </span>
    </aside>
  );
}

/** While playing: the end of run panel, scoreboard and vote banner (the pointer is captured, keys are shown). */
function InGame() {
  const r = runResult.value;
  return (
    <>
      {r && runResultVisible.value && (
        <aside class="run-result-panel" key={r.id} aria-live="polite">
          <RunResultCard result={r} />
        </aside>
      )}
      <VoteBanner />
      {scoreboardOpen.value && <Scoreboard />}
    </>
  );
}

function Boot() {
  return (
    <div class="boot">
      <div class="logo logo-lg">
        TJ<span>game</span>
      </div>
      <div class="boot-text">Loading…</div>
    </div>
  );
}

function ShellInner() {
  const app = useApp();
  const p = phase.value;
  const rootRef = useRef<HTMLDivElement>(null);
  // arrow keys stay inside the topmost layer
  const getRoot = useCallback(() => (document.querySelector('.modal-backdrop') as HTMLElement | null) ?? rootRef.current, []);
  useFocusNav(getRoot, p !== 'playing');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (modal.peek()) return modal.peek()!.resolve(false);
      if (createRoomDialog.peek()) return void (createRoomDialog.value = null);
      if (nameDialog.peek()) return void (nameDialog.value = false);
      switch (phase.peek()) {
        case 'menu':
          if (selectedMap.peek()) selectedMap.value = null;
          else if (tab.peek() !== 'play') tab.value = 'play';
          break;
        case 'paused':
          // browsers do not let Escape capture the mouse again: Resume needs Enter or a click
          if (pauseView.peek() === 'settings') pauseView.value = 'main';
          else if (!app.justPaused) (document.querySelector('.pause-menu .btn-primary') as HTMLElement | null)?.focus();
          break;
        case 'loading':
          if ((loading.peek()?.progress ?? 0) >= 1) {
            loading.value = null;
            phase.value = 'paused';
          }
          break;
        case 'playing':
          if (!app.input.locked) app.pause();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const blocked = !!modal.value || !!createRoomDialog.value || nameDialog.value;
  return (
    <div ref={rootRef} class={`shell phase-${p}`}>
      <div class="shell-main" inert={blocked}>
        {p === 'boot' && <Boot />}
        {p === 'menu' && <Menu />}
        {p === 'loading' && <LoadingScreen />}
        {p === 'paused' && <PauseScreen />}
        {p === 'playing' && <InGame />}
      </div>
      <CreateRoomDialog />
      {nameDialog.value && <NameDialog />}
      <ToastStack />
      <Modal />
    </div>
  );
}

export function Shell({ app }: { app: App }) {
  return (
    <AppContext.Provider value={app}>
      <ShellInner />
    </AppContext.Provider>
  );
}
